import { For, Show, createMemo, createSignal, createUniqueId } from 'solid-js'

import { getLocale, useI18n } from '../../i18n'
import { computeWeightAxisTicks } from '../weight/weightChartUtils'
import ChartValueAxis from './ChartValueAxis'
import { createContainerWidth } from './createContainerWidth'
import type { BucketSize, VolumeBucket } from './metricViewUtils'

type VolumeBarChartProps = {
   buckets: VolumeBucket[]
   bucketSize: BucketSize
   formatTotal: (total: number) => string
   /** Formats a value for hover text; defaults to `formatTotal` (which labels the axis). */
   formatTooltip?: (total: number) => string
   label: string
   /** Optional per-bucket target drawn as a dashed guide (e.g. a prorated monthly goal). */
   target?: number | null
   targetLabel?: string
   /**
    * When set, the part of each bar below this value is drawn in the "below" tone
    * and only the overflow above it in the base tone (e.g. blue up to the target,
    * green past it).
    */
   splitAt?: number | null
   /** Draw each bar as stacked blocks, one per contributing entry. */
   stacked?: boolean
   /** Limits the chart to the most recent buckets that suit its rendered width. */
   visibleBucketCount?: (chartWidth: number) => number
}

const HEIGHT = 180
const PADDING = { left: 84, right: 20, top: 16, bottom: 28 }
const AXIS_TICK_COUNT = 4
/** Same sky tone the weight chart uses for its target guide. */
const TARGET_STROKE = 'var(--chart-target, #38bdf8)'
const BAR_FILL = '#34d399'
/** Sky tone for the portion of a split bar that sits under the target. */
const BAR_FILL_BELOW_SPLIT = '#0ea5e9'
/** Fraction of a bucket column left empty on each side of its bar. */
const BAR_INSET = 0.15
/** Vertical gap between stacked session blocks, in pixels. */
const SEGMENT_GAP = 4

/**
 * Formats a bucket start for the x axis, by bucket size.
 *
 * @param start - Epoch ms at the bucket start.
 * @param size - The bucket size.
 * @returns The label.
 */
function formatBucketLabel(start: number, size: BucketSize) {
   const date = new Date(start)
   if (size === 'month') {
      return date.toLocaleDateString(getLocale(), { month: 'short' })
   }
   return date.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' })
}

/**
 * Chooses how many buckets to skip between date labels so they stay ~56px apart.
 *
 * @param plotWidth - Usable width in pixels.
 * @param bucketCount - Number of buckets.
 * @returns The label stride (1 = label every bucket).
 */
function computeLabelStride(plotWidth: number, bucketCount: number) {
   const maxLabels = Math.max(1, Math.floor(plotWidth / 56))
   return Math.max(1, Math.ceil(bucketCount / maxLabels))
}

type BarSegment = { from: number; to: number }

type BarSegmentProps = {
   segment: BarSegment
   clipId: string
   x: number
   width: number
   /** Gap trimmed from the top of the segment so stacked sessions read as blocks. */
   gap: number
   toY: (total: number) => number
   splitAt: number | null
}

/**
 * Renders one rounded block of a bar. The block is clipped to its rounded
 * outline and painted in the base tone, then the part below `splitAt` (if any)
 * is painted over in the "below" tone, so the split never breaks the corners.
 *
 * @param props - The segment range, geometry, and split value.
 * @returns The segment elements.
 */
const BarSegmentBlock = (props: BarSegmentProps) => {
   /** Top edge of the painted block: the gap is trimmed from the top so it sits between blocks. */
   const top = () => props.toY(props.segment.to) + props.gap
   const height = () => Math.max(0, props.toY(props.segment.from) - top())
   /** Upper edge of the "below" tone: the split, clamped into this segment. */
   const belowTo = () =>
      props.splitAt === null ?
         props.segment.from
      :  Math.min(props.segment.to, Math.max(props.segment.from, props.splitAt))
   const belowHeight = () =>
      Math.max(0, props.toY(props.segment.from) - props.toY(belowTo()))
   return (
      <g>
         <clipPath id={props.clipId}>
            <rect x={props.x} y={top()} width={props.width} height={height()} rx="3" />
         </clipPath>
         <g clip-path={`url(#${props.clipId})`} fill-opacity="0.85">
            <rect
               x={props.x}
               y={top()}
               width={props.width}
               height={height()}
               fill={BAR_FILL}
            />
            <rect
               x={props.x}
               y={props.toY(belowTo())}
               width={props.width}
               height={belowHeight()}
               fill={BAR_FILL_BELOW_SPLIT}
            />
         </g>
      </g>
   )
}

type VolumeBarsProps = {
   buckets: VolumeBucket[]
   bucketSize: BucketSize
   slotWidth: number
   labelEvery: number
   toY: (total: number) => number
   splitAt: number | null
   stacked: boolean
}

/**
 * Splits a bucket into the stacked segments to draw: one per contributing entry
 * when stacked, otherwise a single block for the whole total.
 *
 * @param bucket - The bucket.
 * @param stacked - Whether to draw one block per entry.
 * @returns The segments, bottom first.
 */
function bucketSegments(bucket: VolumeBucket, stacked: boolean): BarSegment[] {
   if (!stacked) {
      return [{ from: 0, to: bucket.total }]
   }
   let from = 0
   return bucket.parts.map(part => {
      const segment = { from, to: from + part.value }
      from = segment.to
      return segment
   })
}

/**
 * Renders one bar per bucket (as stacked session blocks when `stacked`) with a
 * date label every n-th bucket.
 *
 * @param props - Buckets, layout metrics, formatters, and the optional split value.
 * @returns The bar elements.
 */
const VolumeBars = (props: VolumeBarsProps) => {
   const uid = createUniqueId()
   return (
      <For each={props.buckets}>
         {(bucket, index) => {
            const x = () =>
               PADDING.left + index() * props.slotWidth + props.slotWidth * BAR_INSET
            const width = () => props.slotWidth * (1 - 2 * BAR_INSET)
            const segments = () => bucketSegments(bucket, props.stacked)
            return (
               <g>
                  <For each={segments()}>
                     {(segment, segmentIndex) => (
                        <BarSegmentBlock
                           segment={segment}
                           clipId={`${uid}-bar-${index()}-${segmentIndex()}`}
                           x={x()}
                           width={width()}
                           gap={segmentIndex() < segments().length - 1 ? SEGMENT_GAP : 0}
                           toY={props.toY}
                           splitAt={props.splitAt}
                        />
                     )}
                  </For>
                  <Show when={index() % props.labelEvery === 0}>
                     <text
                        x={x() + width() / 2}
                        y={HEIGHT - 8}
                        text-anchor="middle"
                        font-size="11"
                        fill="currentColor"
                     >
                        {formatBucketLabel(bucket.start, props.bucketSize)}
                     </text>
                  </Show>
               </g>
            )
         }}
      </For>
   )
}

type BarTooltipProps = {
   bucket: VolumeBucket
   bucketSize: BucketSize
   /** Left edge and width of the hovered bucket column, in chart pixels. */
   columnLeft: number
   columnWidth: number
   chartWidth: number
   formatValue: (total: number) => string
   /** List the contributing sessions under the total. */
   showParts: boolean
   /** The bucket's target; adds a "remaining" or "reached" line when set. */
   target: number | null
}

/** Space between the hovered bar and its tooltip, in pixels. */
const TOOLTIP_OFFSET = 6

/**
 * Renders the hovered bar's total (and, for stacked bars, each session) as a
 * small card beside the bar — on whichever side has more room, so it never
 * covers the bar being read. Drawn in HTML rather than SVG `<title>`, which
 * browsers show late or not at all (never on touch).
 *
 * @param props - The bucket, its position, and formatting.
 * @returns The tooltip element.
 */
const BarTooltip = (props: BarTooltipProps) => {
   const t = useI18n()
   /** Target minus total: positive while short of it, zero or below once reached. */
   const remaining = () =>
      props.target === null ? null : props.target - props.bucket.total
   /** Anchors the card to the bar's left edge in the right half, else its right edge. */
   const position = () => {
      const barLeft = props.columnLeft + props.columnWidth * BAR_INSET
      const barRight = props.columnLeft + props.columnWidth * (1 - BAR_INSET)
      return barLeft + barRight > props.chartWidth ?
            { right: `${props.chartWidth - barLeft + TOOLTIP_OFFSET}px` }
         :  { left: `${barRight + TOOLTIP_OFFSET}px` }
   }
   return (
      <div
         class="pointer-events-none absolute top-2 z-10 rounded-lg border border-edge bg-surface-raised px-2.5 py-1.5 text-xs text-ink-soft shadow-lg"
         style={position()}
         aria-hidden="true"
      >
         <p class="font-semibold text-ink whitespace-nowrap">
            {formatBucketLabel(props.bucket.start, props.bucketSize)} ·{' '}
            {props.formatValue(props.bucket.total)}
         </p>
         <Show when={remaining() !== null}>
            <Show
               when={(remaining() ?? 0) > 0}
               fallback={
                  <p class="whitespace-nowrap text-emerald-500">
                     {t('Target reached (+{value})', {
                        value: props.formatValue(-(remaining() ?? 0)),
                     })}
                  </p>
               }
            >
               <p class="whitespace-nowrap text-ink-soft">
                  {t('{value} to target', { value: props.formatValue(remaining() ?? 0) })}
               </p>
            </Show>
         </Show>
         <Show when={props.showParts && props.bucket.parts.length > 1}>
            {/* Newest first, so the list reads top-down like the stacked blocks. */}
            <For each={[...props.bucket.parts].reverse()}>
               {part => (
                  <p class="whitespace-nowrap text-ink-muted">
                     {formatBucketLabel(part.timestamp, 'day')} ·{' '}
                     {props.formatValue(part.value)}
                  </p>
               )}
            </For>
         </Show>
      </div>
   )
}

/**
 * Tracks which bucket column the pointer is over. Mouse and pen follow the
 * pointer; touch selects on tap and a second tap on the same column dismisses,
 * since touch has no pointer-leave to rely on.
 *
 * @param slotWidth - Width of one bucket column, in pixels.
 * @param count - Number of buckets.
 * @returns The active index and the pointer handlers to spread on the `<svg>`.
 */
function createColumnHover(slotWidth: () => number, count: () => number) {
   const [activeIndex, setActiveIndex] = createSignal<number | null>(null)

   /**
    * Maps a pointer position to the bucket column under it.
    *
    * @param event - The pointer event on the chart.
    * @returns The bucket index, or null outside the plot.
    */
   const indexAt = (event: PointerEvent) => {
      const svg = event.currentTarget as SVGSVGElement
      const x = event.clientX - svg.getBoundingClientRect().left - PADDING.left
      const index = Math.floor(x / slotWidth())
      return index >= 0 && index < count() ? index : null
   }

   return {
      activeIndex,
      handlers: {
         /**
          * Follows a mouse or pen across the columns.
          *
          * @param event - The pointer event.
          */
         onPointerMove: (event: PointerEvent) => {
            if (event.pointerType !== 'touch') {
               setActiveIndex(indexAt(event))
            }
         },
         /**
          * Selects the tapped or clicked column; a repeat tap dismisses it.
          *
          * @param event - The pointer event.
          */
         onPointerDown: (event: PointerEvent) => {
            const index = indexAt(event)
            const isRepeatTap = event.pointerType === 'touch' && index === activeIndex()
            setActiveIndex(isRepeatTap ? null : index)
         },
         /**
          * Clears the selection when a mouse or pen leaves the chart.
          *
          * @param event - The pointer event.
          */
         onPointerLeave: (event: PointerEvent) => {
            if (event.pointerType !== 'touch') {
               setActiveIndex(null)
            }
         },
      },
   }
}

/**
 * Renders the dashed horizontal target guide across the plot.
 *
 * @param props - The guide's y coordinate and the chart width.
 * @returns The line element.
 */
const TargetGuide = (props: { y: number; chartWidth: number }) => (
   <line
      x1={PADDING.left}
      x2={props.chartWidth - PADDING.right}
      y1={props.y}
      y2={props.y}
      stroke={TARGET_STROKE}
      stroke-width="1.5"
      stroke-dasharray="6 4"
   />
)

/**
 * Builds the chart's vertical scale: the axis ceiling covers the tallest bar and
 * the target, so the guide stays visible even when no bar reaches it.
 *
 * @param buckets - The buckets to plot.
 * @param rawTarget - The per-bucket target, if any.
 * @returns The tallest bar, the valid target, axis ticks, and a value→y mapper.
 */
function createBarScale(
   buckets: () => VolumeBucket[],
   rawTarget: () => number | null | undefined,
) {
   const maxBar = createMemo(() => Math.max(0, ...buckets().map(bucket => bucket.total)))
   const target = createMemo(() => {
      const value = rawTarget()
      return typeof value === 'number' && value > 0 ? value : null
   })
   const maxTotal = createMemo(() => Math.max(maxBar(), target() ?? 0))
   const ticks = createMemo(() =>
      maxTotal() > 0 ? computeWeightAxisTicks(0, maxTotal(), AXIS_TICK_COUNT) : [],
   )
   const plotHeight = HEIGHT - PADDING.top - PADDING.bottom

   /**
    * Maps a value to its y coordinate (the top of a bar of that total).
    *
    * @param total - The value.
    * @returns The y coordinate.
    */
   const toY = (total: number) =>
      HEIGHT - PADDING.bottom - (maxTotal() > 0 ? (total / maxTotal()) * plotHeight : 0)

   return { maxBar, target, ticks, toY }
}

/**
 * Shades the hovered bucket column behind its bar.
 *
 * @param props - The column's left edge and width.
 * @returns The highlight rect.
 */
const ColumnHighlight = (props: { x: number; width: number }) => (
   <rect
      x={props.x}
      y={PADDING.top}
      width={props.width}
      height={HEIGHT - PADDING.top - PADDING.bottom}
      fill="currentColor"
      fill-opacity="0.08"
      rx="4"
   />
)

/**
 * Renders the centred placeholder shown when a range has no bars.
 *
 * @param props - The horizontal centre and the message.
 * @returns The text element.
 */
const EmptyChartMessage = (props: { x: number; text: string }) => (
   <text
      x={props.x}
      y={HEIGHT / 2}
      text-anchor="middle"
      font-size="13"
      fill="currentColor"
   >
      {props.text}
   </text>
)

/**
 * Renders summed volume per bucket (e.g. distance per week) as SVG bars.
 *
 * @param props - The buckets and formatters.
 * @returns The chart element.
 */
const VolumeBarChart = (props: VolumeBarChartProps) => {
   const t = useI18n()
   const container = createContainerWidth()
   const buckets = createMemo(() =>
      props.visibleBucketCount ?
         props.buckets.slice(-props.visibleBucketCount(container.width()))
      :  props.buckets,
   )
   const { maxBar, target, ticks, toY } = createBarScale(buckets, () => props.target)
   const slotWidth = createMemo(
      () =>
         (container.width() - PADDING.left - PADDING.right) /
         Math.max(1, buckets().length),
   )
   const labelEvery = createMemo(() =>
      computeLabelStride(
         container.width() - PADDING.left - PADDING.right,
         buckets().length,
      ),
   )

   const hover = createColumnHover(slotWidth, () => buckets().length)
   const activeIndex = hover.activeIndex
   /** The hovered bucket, only while there are bars to hover. */
   const activeBucket = createMemo(() => {
      const index = activeIndex()
      return index === null || maxBar() <= 0 ? null : (buckets()[index] ?? null)
   })

   return (
      <div
         ref={container.ref}
         class="relative w-full overflow-hidden rounded-xl border border-edge-soft bg-surface/60"
      >
         <svg
            width={container.width()}
            height={HEIGHT}
            viewBox={`0 0 ${container.width()} ${HEIGHT}`}
            class="block text-ink-muted"
            role="img"
            aria-label={props.label}
            {...hover.handlers}
         >
            <Show when={activeBucket()}>
               <ColumnHighlight
                  x={PADDING.left + (activeIndex() ?? 0) * slotWidth()}
                  width={slotWidth()}
               />
            </Show>
            <ChartValueAxis
               ticks={ticks()}
               toY={toY}
               formatValue={props.formatTotal}
               left={PADDING.left}
               right={container.width() - PADDING.right}
            />
            <line
               x1={PADDING.left}
               x2={container.width() - PADDING.right}
               y1={HEIGHT - PADDING.bottom}
               y2={HEIGHT - PADDING.bottom}
               stroke="var(--chart-axis, rgba(148, 163, 184, 0.35))"
            />
            <Show when={target()}>
               {value => <TargetGuide y={toY(value())} chartWidth={container.width()} />}
            </Show>
            <Show
               when={maxBar() > 0}
               fallback={
                  <EmptyChartMessage
                     x={container.width() / 2}
                     text={t('No entries in this range yet.')}
                  />
               }
            >
               <VolumeBars
                  buckets={buckets()}
                  bucketSize={props.bucketSize}
                  slotWidth={slotWidth()}
                  labelEvery={labelEvery()}
                  toY={toY}
                  splitAt={props.splitAt ?? null}
                  stacked={props.stacked ?? false}
               />
            </Show>
         </svg>
         <Show when={activeBucket()}>
            {bucket => (
               <BarTooltip
                  bucket={bucket()}
                  bucketSize={props.bucketSize}
                  columnLeft={PADDING.left + (activeIndex() ?? 0) * slotWidth()}
                  columnWidth={slotWidth()}
                  chartWidth={container.width()}
                  formatValue={props.formatTooltip ?? props.formatTotal}
                  showParts={props.stacked ?? false}
                  target={target()}
               />
            )}
         </Show>
      </div>
   )
}

export default VolumeBarChart
